import { BaseServices, IScore } from "./base";
import { WorkerClient } from "./workerClient";

// Cloud function name → REST endpoint mapping
async function cloudDispatch<T>(name: string, params: Record<string, unknown> | undefined,
    client: WorkerClient): Promise<T> {
    switch (name) {
        case "getConfig":
        case "getMapDetails":
            return client.get<T>("/game/config");

        case "getUserPlayer": {
            const player = await client.get<Record<string, unknown>>("/game/player");

            // Cache location locally — for all users, current position is client-side
            if (player["location"] != null) {
                client.storeLocation(player["location"] as { regionId: number; tileId: number });
            }

            // Augment with username so UserPlayer.anonymous getter works correctly:
            // anonymous users get their userId (no "@"), named users get their email.
            return {
                ...player,
                username: client.isAnonymous ? (client.userId ?? "") : (client.email ?? ""),
                roles: []
            } as T;
        }

        case "getRegion":
            return client.get<T>(`/game/region/${(params as { regionId: number }).regionId}`);

        case "placeLetters": {
            const result = await client.post<Record<string, unknown>>("/game/place-letters", params);

            if (result["location"] != null) {
                client.storeLocation(result["location"] as { regionId: number; tileId: number });
            }
            return result as T;
        }

        case "movePlayer": {
            // Navigation is client-side only — no server call needed.
            // Store the new position locally so the next poll targets the right region.
            const loc = params as { regionId: number; tileId: number };

            client.storeLocation(loc);
            return { location: loc } as T;
        }

        case "shovelTile": {
            const loc = (params as {location: {regionId: number; tileId: number}}).location;
            return client.post<T>("/game/shovel-tile", { regionId: loc.regionId, tileId: loc.tileId });
        }

        case "getGlobalLeaderboards": {
            const [daily, weekly, monthly] = await Promise.all([
                client.get<{results: unknown[]}>("/game/leaderboard?period=daily"),
                client.get<{results: unknown[]}>("/game/leaderboard?period=weekly"),
                client.get<{results: unknown[]}>("/game/leaderboard?period=monthly")
            ]);
            return { daily: daily.results, weekly: weekly.results, monthly: monthly.results } as T;
        }

        case "getLeague": {
            const { leagueId } = params as { leagueId: string };
            return client.get<T>(`/game/leagues/${leagueId}`);
        }

        case "getGlobalHighScores":
            return {} as T;

        case "getPlayerScores":
            return [] as T;

        case "getWordsScore":
            return client.post<T>("/game/words-score", params);

        case "selectNickname":
            return client.post<T>("/game/nickname", params);

        case "getNicknames": {
            const ids = ((params as {playerIds?: string[]})?.playerIds ?? []).join(",");
            return client.get<{results: T}>(`/game/nicknames?ids=${ids}`).then(r => r.results as T);
        }

        case "getPlayerLeaderboards": {
            const [daily, weekly, monthly] = await Promise.all([
                client.get<{results: unknown[]}>("/game/leaderboard/player?period=daily"),
                client.get<{results: unknown[]}>("/game/leaderboard/player?period=weekly"),
                client.get<{results: unknown[]}>("/game/leaderboard/player?period=monthly")
            ]);
            return { daily: daily.results, weekly: weekly.results, monthly: monthly.results } as T;
        }

        case "getPlayerPerks":
            return client.get<T>("/game/perks");

        case "getUserRoles":
            return client.get<{roles: T}>("/game/roles").then(r => r.roles as T);

        case "getAchievements":
            return client.get<T>("/game/achievements");

        case "addUserInvite":
            return client.post<T>("/game/invite", params);

        case "createCompetition":
            return client.post<T>("/game/competitions", params);

        case "joinCompetition": {
            const { code } = params as { code: string };
            return client.post<T>(`/game/competitions/join/${code}`, {});
        }

        case "getPlayerCompetitions":
            return client.get<T>("/game/competitions");

        case "getCompetitionByCode": {
            const { code } = params as { code: string };
            return client.get<T>(`/game/competitions/by-code/${code}`);
        }

        case "getCompetitionStandings": {
            const { id } = params as { id: string };
            return client.get<T>(`/game/competitions/${id}`);
        }

        default:
            throw new Error(`Unknown cloud function: ${name}`);
    }
}

const SUPPORTED_APIS = ["cloud", "logIn", "logOut", "signUp", "resetPassword", "userName", "authenticate", "movePlayer"];

export class WorkerServices extends BaseServices {
    private readonly _client = WorkerClient.instance;

    async initialise(): Promise<boolean> {
        const params = new URLSearchParams(window.location.search);
        const googleStatus = params.get("google");

        if (googleStatus === "ok") {
            this._client.storeTokens({
                accessToken:  params.get("accessToken")!,
                refreshToken: params.get("refreshToken")!,
                userId:       params.get("userId")!,
                isAnonymous:  false,
                email:        params.get("email")
            });
            window.history.replaceState({}, "", window.location.pathname);
        } else if (googleStatus === "error" || googleStatus === "cancelled") {
            window.history.replaceState({}, "", window.location.pathname);
        }

        const joinCode = params.get("join");
        if (joinCode) {
            localStorage.setItem("pendingCompetitionJoin", joinCode);
            window.history.replaceState({}, "", window.location.pathname);
        }

        return true;
    }

    get name(): string {
        return "Worker";
    }

    get authenticated(): boolean {
        return this._client.authenticated;
    }

    getLoggedIn(): boolean {
        return this._client.authenticated;
    }

    getSupports(api: string): boolean {
        return SUPPORTED_APIS.includes(api);
    }

    getUserName(): string | null {
        // Returns null for anonymous users so the UI can prompt to register
        return this._client.isAnonymous ? null : this._client.email;
    }

    async cloud<T = unknown>(name: string, parameters?: Record<string, unknown>): Promise<T> {
        return cloudDispatch<T>(name, parameters, this._client);
    }

    async logIn(props: Record<string, unknown>): Promise<boolean> {
        if (props["username"]) {
            const data = await this._client.post<{
                accessToken: string; refreshToken: string; userId: string; isAnonymous: boolean;
            }>("/auth/login", { email: props["username"], password: props["password"] });

            this._client.storeTokens({ ...data, email: props["username"] as string });
        } else {
            const data = await this._client.post<{
                accessToken: string; refreshToken: string; userId: string; isAnonymous: boolean;
            }>("/auth/anonymous");

            this._client.storeTokens({ ...data, email: null });
        }
        return true;
    }

    logOut(): void {
        this._client.clearTokens();
    }

    async signUp(props: Record<string, unknown>): Promise<boolean> {
        if (this._client.isAnonymous) {
            // Upgrade anonymous account — preserves player data, keeps existing tokens
            await this._client.post("/auth/upgrade", { email: props["username"], password: props["password"] });
            localStorage.setItem("worker.isAnonymous", "false");
            localStorage.setItem("worker.email", props["username"] as string);
        } else {
            const data = await this._client.post<{
                accessToken: string; refreshToken: string; userId: string; isAnonymous: boolean;
            }>("/auth/signup", { email: props["username"], password: props["password"] });

            this._client.storeTokens({ ...data, email: props["username"] as string });
        }
        return true;
    }

    async resetPassword(email: string): Promise<boolean> {
        await this._client.post("/auth/reset-password", { email });
        return true;
    }
}
